import {
	allDates,
	cumulativeAt,
	formatMoney,
	type ChartSeries,
} from './ledger.ts'

interface LedgerChartProps {
	series: ChartSeries[]
	/** Bold one series, fade the rest (used by the time-travel cursor). */
	highlightKey?: string | null
	height?: number
}

// Hand-rolled SVG rather than a chart library — the house convention (see the
// other demos' charts): one viewBox, non-scaling strokes, currentColor for the
// chrome so dark mode needs no special-casing. Series colors stay literal hex
// because they identify a committed version, not a theme role.
export function LedgerChart({
	series,
	highlightKey,
	height = 288,
}: LedgerChartProps) {
	const dates = allDates(series)

	if (series.length === 0 || dates.length === 0) {
		return (
			<div
				style={{ height }}
				className="text-muted-foreground flex items-center justify-center rounded-md border border-dashed text-sm"
			>
				No versions yet — commit one to plot it.
			</div>
		)
	}

	const W = 100
	const H = 40
	const pad = 3
	const points = series.map((s) => dates.map((d) => cumulativeAt(s.lines, d)))
	const vals = points.flat()
	const min = Math.min(...vals, 0)
	const max = Math.max(...vals, 0)
	const x = (i: number) =>
		pad + (i / Math.max(dates.length - 1, 1)) * (W - 2 * pad)
	const y = (v: number) =>
		pad + (1 - (v - min) / (max - min || 1)) * (H - 2 * pad)
	const path = (vs: number[]) =>
		vs
			.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(2)} ${y(v).toFixed(2)}`)
			.join(' ')

	return (
		<div className="rounded-md border p-3">
			<svg
				viewBox={`0 0 ${W} ${H}`}
				preserveAspectRatio="none"
				style={{ height: height - 58 }}
				className="w-full"
				role="img"
				aria-label="Cumulative net over entry date, one line per committed version"
			>
				{/* zero line */}
				<line
					x1={pad}
					x2={W - pad}
					y1={y(0)}
					y2={y(0)}
					className="text-border"
					stroke="currentColor"
					strokeWidth={0.6}
					vectorEffect="non-scaling-stroke"
				/>
				{series.map((s, si) => {
					const dimmed = highlightKey && s.key !== highlightKey
					return (
						<path
							key={s.key}
							d={path(points[si] ?? [])}
							fill="none"
							stroke={s.color}
							strokeWidth={highlightKey && s.key === highlightKey ? 2.5 : 1.5}
							strokeOpacity={dimmed ? 0.25 : 1}
							vectorEffect="non-scaling-stroke"
						/>
					)
				})}
			</svg>
			<div className="text-muted-foreground mt-2 flex items-start justify-between gap-3 text-[11px]">
				<div className="flex flex-wrap gap-x-3 gap-y-1">
					{series.map((s) => (
						<span
							key={s.key}
							style={{ color: s.color }}
							className={
								highlightKey && s.key !== highlightKey
									? 'opacity-40'
									: undefined
							}
						>
							— {s.label}
						</span>
					))}
				</div>
				<span className="shrink-0 tabular-nums">
					{formatMoney(min)} … {formatMoney(max)}
				</span>
			</div>
			<div className="text-muted-foreground mt-1 flex justify-between text-[11px] tabular-nums">
				<span>{dates[0]}</span>
				<span>{dates[dates.length - 1]}</span>
			</div>
		</div>
	)
}
